// Mobile-optimized new survey form
// Large touch-friendly inputs, simple flow

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import useSurveyStore from '../../stores/surveyStore'
import useSettingsStore from '../../stores/settingsStore'

const SURVEY_LEVELS = [
  { value: 1, label: 'Level 1', description: 'Basic visual inspection' },
  { value: 2, label: 'Level 2', description: 'Standard home survey' },
  { value: 3, label: 'Level 3', description: 'Full building survey' }
]

export default function MobileNewSurvey() {
  const navigate = useNavigate()
  const createSurvey = useSurveyStore(state => state.createSurvey)
  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)

  const [formData, setFormData] = useState({
    propertyName: '',
    address: '',
    postcode: '',
    surveyorName: defaultSurveyor || '',
    surveyLevel: 2
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleSubmit = async () => {
    // Validate
    if (!formData.propertyName.trim()) {
      setError('Please enter a property name')
      return
    }
    if (!formData.address.trim()) {
      setError('Please enter an address')
      return
    }
    if (!formData.postcode.trim()) {
      setError('Please enter a postcode')
      return
    }
    if (!formData.surveyorName.trim()) {
      setError('Please enter surveyor name')
      return
    }

    setLoading(true)
    try {
      const survey = await createSurvey(formData)
      navigate(`/survey/${survey.id}`)
    } catch (err) {
      setError('Failed to create survey. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div className="bg-primary-700 text-white px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 hover:bg-primary-600 rounded-lg"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-semibold">New Survey</h1>
      </div>

      {/* Form - scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Property Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Property Name *
          </label>
          <input
            type="text"
            value={formData.propertyName}
            onChange={(e) => handleChange('propertyName', e.target.value)}
            placeholder="e.g., Smith Residence"
            className="w-full px-4 py-3 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Address *
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="e.g., 123 High Street, London"
            className="w-full px-4 py-3 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Postcode */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Postcode *
          </label>
          <input
            type="text"
            value={formData.postcode}
            onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
            placeholder="e.g., SW1A 1AA"
            className="w-full px-4 py-3 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
          />
        </div>

        {/* Surveyor */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Surveyor Name *
          </label>
          <input
            type="text"
            value={formData.surveyorName}
            onChange={(e) => handleChange('surveyorName', e.target.value)}
            placeholder="Your name"
            className="w-full px-4 py-3 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Survey Level */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Survey Level
          </label>
          <div className="grid grid-cols-3 gap-2">
            {SURVEY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => handleChange('surveyLevel', level.value)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  formData.surveyLevel === level.value
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <div className="font-bold text-lg">{level.value}</div>
                <div className="text-xs">{level.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Bottom action button */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-slate-200">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white text-lg font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 active:scale-98 transition-all"
        >
          {loading ? (
            <span>Creating...</span>
          ) : (
            <>
              <span>Start Survey</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}
